import React, { createContext, useContext, useState } from 'react';
import { Row, Col, Card, Button, Form, Container } from 'react-bootstrap';
import { Check, Copy } from 'lucide-react';
import ReportHeader from '../ReportHeader';
import { LoadingState, ErrorState } from '../PageState';
import { useIsMobile } from '../../hooks/useMediaQuery';

/*
 * The chart theme lives in components/chartTheme.js; these re-exports keep older imports working.
 * New charts should spread the props from useChartTheme() instead.
 */
export { chartColor, useThemeRedraw, tooltipBox, useChartTheme } from '../chartTheme';

/** Chart colour tokens (CSS custom property names): one emphasis series against grey context. */
export const CHART_COLORS = {
  accent: '--chart-emphasis',
  emphasis: '--chart-emphasis',
  context: '--chart-context',
  neutral: '--chart-neutral',
};

/** Categories in order (darkest first), then the neutral for anything past three. */
export const CHART_PALETTE = [
  '--chart-ordinal-1',
  '--chart-ordinal-2',
  '--chart-ordinal-3',
  '--chart-neutral',
];

/*
 * Phone chart settings: a shorter chart whose side margins and Y axis hand their width back
 * to the plot. Spread into the chart elements after the theme props, as
 * `{...theme.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})}`, so the desktop props stay as they are.
 * The theme's ticks are already 12px, so these leave the tick style alone.
 */
export const PHONE_CHART_HEIGHT = 280;
export const PHONE_CHART_MARGIN = { top: 10, right: 8, left: 0, bottom: 0 };
const compactNumber = new Intl.NumberFormat('en-GB', { notation: 'compact', maximumSignificantDigits: 3 });
export const PHONE_Y_AXIS = { width: 40, tickFormatter: (v) => compactNumber.format(v) };
/** Every category label, instead of Recharts dropping the ones that would collide. */
export const PHONE_X_AXIS = { interval: 0 };

/** Legend entries in body text colour; Recharts colours each one like its series, which the paler tokens make hard to read. */
export const legendLabel = (value) => <span style={{ color: 'var(--fg-default)' }}>{value}</span>;

/** "Wednesday" to "Wed", so all five weekday labels fit across a phone. */
export const shortDay = (day) => String(day).slice(0, 3);

/** The entry with the largest value of `key`, the first one on a tie. */
export const maxBy = (rows, key) => rows.reduce((best, r) => (best === null || r[key] > best[key] ? r : best), null);
/** The entry with the smallest value of `key`, the first one on a tie. */
export const minBy = (rows, key) => rows.reduce((best, r) => (best === null || r[key] < best[key] ? r : best), null);

/** "a, b and c". */
export const listJoin = (items) => (items.length < 2
  ? items.join('')
  : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

// Inside a StatRow the row's gutters space the tiles; elsewhere (the home page) a tile keeps its own bottom margin
const StatRowContext = createContext(false);

/** A row of stat tiles with gutters on every side, so the bordered tiles never touch. */
export const StatRow = ({ children }) => (
  <StatRowContext.Provider value>
    <Row className="g-3 g-md-4 mb-4">{children}</Row>
  </StatRowContext.Provider>
);

/**
 * A single KPI tile matching the Reports page stat cards.
 * Values are near-black; variant="primary" marks the row's one headline figure in accent blue.
 * On phones the tiles sit two across (xs={4} for a row of three).
 */
export const StatCard = ({ label, value, sub, variant, xs = 6 }) => {
  const inStatRow = useContext(StatRowContext);
  return (
    <Col xs={xs} md={3} className={inStatRow ? undefined : 'mb-2 mb-md-3'}>
      <Card className="shadow-sm h-100 stat-tile">
        <Card.Body className="text-center">
          <h6 className="text-muted mb-2">{label}</h6>
          <h2 className={`${variant === 'primary' ? 'text-primary ' : ''}mb-0`} style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</h2>
          {sub && <small className="text-muted">{sub}</small>}
        </Card.Body>
      </Card>
    </Col>
  );
};

/** A small select with its label linked to it, for the controls in an insight page's header. */
export const ControlSelect = ({ id, label, value, onChange, style, children }) => {
  const isMobile = useIsMobile();
  return (
    <Form.Group controlId={id}>
      <Form.Label className="small text-muted mb-1">{label}</Form.Label>
      <Form.Select size="sm" value={value} onChange={onChange} style={{ width: isMobile ? '100%' : 'auto', ...style }}>
        {children}
      </Form.Select>
    </Form.Group>
  );
};

/** A copy-to-clipboard block of the headline numbers, ready to paste into a post. */
export const CopyStats = ({ text }) => {
  const [status, setStatus] = useState(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus('copied');
    } catch {
      setStatus('failed');
    }
    setTimeout(() => setStatus(null), 2000);
  };
  return (
    <Card className="shadow-sm mt-4">
      <Card.Header className="bg-light d-flex justify-content-between align-items-center gap-2">
        <h5 className="mb-0">Post copy</h5>
        <div className="d-flex align-items-center gap-2">
          {status === 'failed' && <span className="save-status is-error">Could not copy, select the text instead</span>}
          <Button size="sm" variant="outline-secondary" onClick={copy}>
            {status === 'copied'
              ? <><Check size={14} className="me-1" aria-hidden="true" />Copied</>
              : <><Copy size={14} className="me-1" aria-hidden="true" />Copy text</>}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{text}</pre>
      </Card.Body>
    </Card>
  );
};

/**
 * Shared page frame for every insight report: title, back link, controls, and the loading, error
 * and empty states. Children (the stat tiles and chart) render only when there are figures to show:
 * a failed load shows the error with Try again rather than zeros, and a reload dims the old figures
 * instead of blanking them.
 *
 * @param {boolean} ready - true once the first response has arrived.
 * @param {boolean} empty - true when the response holds nothing to chart.
 */
const InsightPage = ({
  title, description, controls, loading, error, onRetry, ready, empty, emptyMessage = 'No data in this window', children,
}) => {
  let body;
  if (error) {
    body = <Card className="shadow-sm"><ErrorState inCard message={error} onRetry={onRetry} /></Card>;
  } else if (!ready || (empty && loading)) {
    body = <Card className="shadow-sm"><LoadingState /></Card>;
  } else if (empty) {
    body = <Card className="shadow-sm"><div className="p-4 text-center text-muted">{emptyMessage}</div></Card>;
  } else {
    body = (
      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 120ms ease-out' }} aria-busy={loading}>
        {children}
      </div>
    );
  }

  return (
    <Container fluid className="py-4">
      <ReportHeader
        title={title}
        description={description}
        backTo="/data-insights"
        backLabel="Back to data insights"
        actions={controls && <div className="d-flex flex-wrap gap-3 align-items-end filter-bar w-100">{controls}</div>}
      />
      {body}
    </Container>
  );
};

export default InsightPage;
